<template>
  <dss-template>
    <div id="personal-info" class="container page">

      <h2 class="title title-dash">{{ $t('dss.page.personal.title') }}</h2>

      <stihl-overlay :active="state === 'loading'" :spinner="true">
        <form @submit.prevent="save">

          <stihl-section :title="$t('dss.page.personal.secPersonal')" class="mt-5">
            <div class="row">
              <group-select id="salutation"
                            class="col-2 mb-3"
                            :label="$t('dss.label.salutation')"
                            v-model="v$.form.salutation.$model"
                            :options="salutations"
                            @keypress.enter="save"/>
              <group-input id="firstname" class="col-5 mb-3"
                           :required="true"
                           :label="$t('dss.label.firstname')"
                           v-model="v$.form.firstName.$model"
                           :validation-errors="v$.form.firstName.$errors"
                           @keyup.enter="save"/>
              <group-input id="lastname" class="col-5 mb-3"
                           :required="true"
                           :label="$t('dss.label.lastname')"
                           v-model="v$.form.lastName.$model"
                           :validation-errors="v$.form.lastName.$errors"
                           @keyup.enter="save"/>
            </div>
            <group-input id="email" class="mb-3"
                         :disabled="true"
                         :label="$t('dss.label.email')"
                         v-model="form.email"/>
            <group-select id="lang"
                          :label="$t('dss.label.language')"
                          v-model="v$.form.preferredLanguage.$model"
                          :options="languages"
                          @keypress.enter="save"/>
          </stihl-section>

          <div class="buttons">
            <button id="save" type="button" class="btn btn-secondary" @click="save" :disabled="state!=='ready'">
              {{ $t('dss.button.save') }}
            </button>
          </div>

          <stihl-section :title="$t('dss.page.personal.secOrganization')" class="mt-5">
            <div class="row">
              <group-input id="org" class="col mb-3"
                           :disabled="true"
                           :label="$t('dss.label.organization')"
                           v-model="form.organizationRefObject.name"/>
              <group-input id="crmNo" class="col-auto"
                           :disabled="true"
                           :label="$t('dss.label.CRMAccountNumber')"
                           v-model="form.organizationRefObject.CRMAccountNumber"/>
            </div>
          </stihl-section>

        </form>
      </stihl-overlay>

    </div>
  </dss-template>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DssTemplate from '@/components/layout/DssTemplate.vue';
import StihlSection from '@/components/ui/StihlSection.vue';
import GroupInput from '@/components/ui/GroupInput.vue';
import GroupSelect, { OptionItem } from '@/components/ui/GroupSelect.vue';
import { languageCodes } from '@/lang';
import useVuelidate from '@vuelidate/core';
import { languageCode, minLength, required } from '@/components/validators';
import Backend, { ChangeRequest, SalutationCode, UserDTO } from '@/components/backend';

const strNaturalOrder = function (a: OptionItem, b: OptionItem) {
  return a.label < b.label ? -1 : a.label > b.label ? 1 : 0;
};

// normalize string value, so that <null>, <undefined> & <''> are equal
const norm = (x?: string) => (x ? x : null);

export default defineComponent({
  name: 'PersonalInfoPage',
  components: { DssTemplate, GroupInput, GroupSelect, StihlSection },
  data() {
    return {
      form: {
        salutation: undefined as SalutationCode | undefined,
        firstName: '',
        lastName: '',
        email: '',
        organizationRefObject: {
          name: '',
          CRMAccountNumber: '',
        },
        preferredLanguage: '',
      },
      user: undefined as UserDTO | undefined, // loaded user data
      state: 'loading' as 'ready' | 'loading',
    };
  },
  methods: {
    resetPage() {
      this.state = 'loading';
    },

    save() {
      if (this.state === 'loading') return; // must wait

      if (this.v$.$invalid) return console.error('validation failed', this.v$.$errors);

      const changes: ChangeRequest[] = [];
      if (norm(this.user?.salutation) !== norm(this.form.salutation)) {
        changes.push(this.change('/salutation', this.form.salutation ?? ''));
      }
      if (norm(this.user?.firstName) !== norm(this.form.firstName)) {
        changes.push(this.change('/firstName', this.form.firstName));
      }
      if (norm(this.user?.lastName) !== norm(this.form.lastName)) {
        changes.push(this.change('/lastName', this.form.lastName));
      }
      if (norm(this.user?.preferredLanguage) !== norm(this.form.preferredLanguage)) {
        changes.push(this.change('/preferredLanguage', this.form.preferredLanguage));
      }

      if (changes.length === 0) {
        this.$toast.warning(this.$t('dss.page.personal.msg.noChange'));
        return false;
      }

      this.state = 'loading';
      Backend.validateToken()
          .then(api => api.updateUser(changes)
              .then(_ => {
                this.$toast.info(this.$t('dss.page.personal.msg.updated'));
                //this.user = { ...this.form }; // clone object
                if (this.user) Object.assign(this.user, this.form); // copy attributes
              })
              .catch(_ => this.$toast.error(this.$t('dss.page.personal.msg.updateError')))
              .finally(() => this.state = 'ready'));

    },

    change(path: string, value: string | null): ChangeRequest {
      return value ? { op: 'add', path, value } : { op: 'remove', path };
    }
  },
  computed: {
    salutations() {
      return [
        { key: '', label: '-' },
        { key: 'male', label: this.$t('dss.label.male') },
        { key: 'female', label: this.$t('dss.label.female') },
      ];
    },
    languages() {
      const list = [] as OptionItem[];
      languageCodes.forEach(c =>
          list.push({ 'key': c, 'label': this.$t('dss.lang.' + c) })
      );
      list.sort(strNaturalOrder);
      list.unshift({ key: '', label: this.$t('dss.label.select') });
      return list;
    }
  },
  mounted() {
    this.resetPage();

    Backend.validateToken()
        .then(api => api.loadUserProfile()
            .then((r) => {
              Object.assign(this.form, r.data); // copy attributes
              this.user = r.data;
            })
            .catch(_ => {
              this.$toast.error(this.$t('dss.page.personal.msg.userLoadErr'));
            })
            .finally(() => this.state = 'ready'));
  },
  setup: () => ({ v$: useVuelidate() }),
  validations() {
    return {
      form: {
        // must match the fields in 'this.form'
        salutation: {},
        firstName: { required, minLength: minLength(1) },
        lastName: { required, minLength: minLength(1) },
        preferredLanguage: { languageCode },
      }
    };
  },
});
</script>
